// Propiedades ligadas a un ticket (N:M, tabla `tickets_propiedades`, migración
// 20261020184317_tickets_propiedades.sql en sozu-supabase-migrations).
//
// `tickets.id_propiedad` / `id_cuenta_cobranza` / `id_proyecto` / `inmueble` se conservan como la
// propiedad PRINCIPAL (la primera): la app de clientes (EF cliente-tickets) y las demás EFs las
// siguen leyendo. La tabla de unión agrega las demás sin romper a nadie.
//
// La cadena propiedad -> proyecto es la misma que usa la app de clientes (`unidadYProyecto` en
// sozu-edge-functions/_shared/tickets.ts): `propiedades` NO tiene `id_proyecto` y su
// `id_edificio` viene null en las vendidas; el único camino es
// id_edificio_modelo -> edificios_modelos -> edificios -> proyectos. Etiqueta "Proyecto - U-101".
//
// La cadena cliente -> propiedad también es la de la app: compradores (activo) -> cuentas_cobranza
// (id_propiedad, o la de su oferta si viene null).
import { supabase } from "@/integrations/supabase/client";
import type { PropiedadRef } from "./tickets-data";

const sb = supabase as any;

/** Tope de resultados del buscador (hay decenas de miles de propiedades). */
export const LIMITE_PROPIEDADES = 50;

/** Mismo formato que `unidadYProyecto` de la app de clientes. */
export function etiquetaPropiedad(unidad: string | null | undefined, proyecto: string | null | undefined): string {
  const u = unidad != null && String(unidad).trim() !== "" ? String(unidad).trim() : null;
  if (u == null) return proyecto || "Unidad sin número";
  return proyecto ? `${proyecto} - U-${u}` : `U-${u}`;
}

const SELECT_PROPIEDAD =
  "id, numero_propiedad, edificios_modelos!fk_propiedades_edificio_modelo(edificios!fk_edificios_modelos_edificio(id_proyecto, proyectos!fk_edificios_proyecto(nombre)))";

function aRef(p: any, idCuentaCobranza?: number | string | null): PropiedadRef {
  const ed = p?.edificios_modelos?.edificios;
  const idProyecto = ed?.id_proyecto != null ? String(ed.id_proyecto) : null;
  return {
    id: String(p.id),
    etiqueta: etiquetaPropiedad(p.numero_propiedad, ed?.proyectos?.nombre ?? null),
    idCuentaCobranza: idCuentaCobranza != null ? String(idCuentaCobranza) : null,
    idProyecto,
  };
}

/** Etiqueta + proyecto de varias propiedades (un solo request). */
export async function describirPropiedades(
  ids: (string | number)[],
  signal?: AbortSignal,
): Promise<Map<string, PropiedadRef>> {
  const unicos = Array.from(new Set(ids.map((i) => Number(i)).filter(Number.isFinite)));
  const out = new Map<string, PropiedadRef>();
  if (!unicos.length) return out;
  // Lotes de 200: la lista viaja en la URL.
  for (let i = 0; i < unicos.length; i += 200) {
    let q = sb.from("propiedades").select(SELECT_PROPIEDAD).in("id", unicos.slice(i, i + 200));
    if (signal) q = q.abortSignal(signal);
    const { data, error } = await q;
    if (error) throw error;
    for (const p of data ?? []) out.set(String(p.id), aRef(p));
  }
  return out;
}

/**
 * Propiedades de unas personas como compradoras (cuentas activas). Una por propiedad: si la
 * persona tiene la cuenta de la unidad y una hija (mantenimiento), gana la que no tiene padre.
 */
async function propiedadesDePersonas(personaIds: number[], signal?: AbortSignal): Promise<PropiedadRef[]> {
  if (!personaIds.length) return [];
  let qc = sb
    .from("compradores")
    .select("id_cuenta_cobranza")
    .in("id_persona", personaIds)
    .eq("activo", true)
    .limit(500);
  if (signal) qc = qc.abortSignal(signal);
  const { data: comp, error: ec } = await qc;
  if (ec) throw ec;
  const cuentaIds = Array.from(new Set((comp ?? []).map((c: any) => Number(c.id_cuenta_cobranza))));
  if (!cuentaIds.length) return [];

  let qcc = sb
    .from("cuentas_cobranza")
    .select("id, id_propiedad, id_oferta, id_cuenta_cobranza_padre")
    .in("id", cuentaIds)
    .eq("activo", true);
  if (signal) qcc = qcc.abortSignal(signal);
  const { data: cuentas, error: ecc } = await qcc;
  if (ecc) throw ecc;

  // Fallback de la app: sin id_propiedad en la cuenta, la de su oferta.
  const sinProp = (cuentas ?? []).filter((c: any) => c.id_propiedad == null && c.id_oferta != null);
  const propDeOferta = new Map<number, number>();
  if (sinProp.length) {
    let qo = sb.from("ofertas").select("id, id_propiedad").in("id", sinProp.map((c: any) => c.id_oferta));
    if (signal) qo = qo.abortSignal(signal);
    const { data: ofs } = await qo;
    for (const o of ofs ?? []) if (o.id_propiedad != null) propDeOferta.set(Number(o.id), Number(o.id_propiedad));
  }

  const cuentaPorProp = new Map<number, { cuenta: number; hija: boolean }>();
  for (const c of cuentas ?? []) {
    const pid = c.id_propiedad != null ? Number(c.id_propiedad) : propDeOferta.get(Number(c.id_oferta));
    if (pid == null) continue;
    const hija = c.id_cuenta_cobranza_padre != null;
    const prev = cuentaPorProp.get(pid);
    if (!prev || (prev.hija && !hija)) cuentaPorProp.set(pid, { cuenta: Number(c.id), hija });
  }
  if (!cuentaPorProp.size) return [];
  const desc = await describirPropiedades(Array.from(cuentaPorProp.keys()), signal);
  return Array.from(cuentaPorProp.entries())
    .map(([pid, { cuenta }]) => {
      const d = desc.get(String(pid));
      return d ? { ...d, idCuentaCobranza: String(cuenta) } : null;
    })
    .filter(Boolean) as PropiedadRef[];
}

/** Propiedades sugeridas para los solicitantes del ticket (entidades_relacionadas.id). */
export async function propiedadesDeSolicitantes(
  entidadIds: string[],
  signal?: AbortSignal,
): Promise<PropiedadRef[]> {
  const ids = entidadIds.map(Number).filter(Number.isFinite);
  if (!ids.length) return [];
  let q = sb.from("entidades_relacionadas").select("id_persona").in("id", ids);
  if (signal) q = q.abortSignal(signal);
  const { data, error } = await q;
  if (error) throw error;
  const personaIds = Array.from(
    new Set((data ?? []).map((e: any) => e.id_persona).filter((v: any) => v != null).map(Number)),
  ) as number[];
  const refs = await propiedadesDePersonas(personaIds, signal);
  return refs.sort((a, b) => a.etiqueta.localeCompare(b.etiqueta, "es", { numeric: true }));
}

// Quita lo que rompe filtros de PostgREST (coma, paréntesis) y comodines.
function terminoSeguro(t: string): string {
  return t.replace(/[,()%*\\]/g, " ").replace(/\s+/g, " ").trim();
}

/**
 * Búsqueda en servidor, topada a `limite`. Acepta "408", "Bottura", "Bottura 408" o el nombre
 * del cliente (comprador). Los tokens con dígitos se buscan en el número de unidad y los de
 * letras en el nombre del proyecto; si no hay proyecto que coincida, se prueban como cliente.
 */
export async function buscarPropiedades(
  texto: string,
  { limite = LIMITE_PROPIEDADES, signal }: { limite?: number; signal?: AbortSignal } = {},
): Promise<PropiedadRef[]> {
  const term = terminoSeguro(texto);
  if (!term) return [];
  const tokens = term.split(" ");
  const unidad = tokens.filter((t) => /\d/.test(t)).join(" ");
  const palabras = tokens.filter((t) => !/\d/.test(t)).join(" ");

  const out = new Map<string, PropiedadRef>();
  const meter = (refs: Iterable<PropiedadRef>) => {
    for (const r of refs) if (!out.has(r.id) && out.size < limite) out.set(r.id, r);
  };

  // 1) Por proyecto (+ unidad si viene).
  let proyectoCoincide = false;
  if (palabras) {
    let qp = sb.from("proyectos").select("id").ilike("nombre", `%${palabras}%`).limit(5);
    if (signal) qp = qp.abortSignal(signal);
    const { data: proys, error: ep } = await qp;
    if (ep) throw ep;
    const proyIds = (proys ?? []).map((p: any) => Number(p.id));
    if (proyIds.length) {
      proyectoCoincide = true;
      let qe = sb.from("edificios").select("id").in("id_proyecto", proyIds);
      if (signal) qe = qe.abortSignal(signal);
      const { data: eds, error: ee } = await qe;
      if (ee) throw ee;
      const edIds = (eds ?? []).map((e: any) => Number(e.id));
      if (edIds.length) {
        let qm = sb.from("edificios_modelos").select("id").in("id_edificio", edIds);
        if (signal) qm = qm.abortSignal(signal);
        const { data: ems, error: em } = await qm;
        if (em) throw em;
        const emIds = (ems ?? []).map((e: any) => Number(e.id));
        if (emIds.length) {
          let q = sb
            .from("propiedades")
            .select(SELECT_PROPIEDAD)
            .in("id_edificio_modelo", emIds)
            .order("numero_propiedad")
            .limit(limite);
          if (unidad) q = q.ilike("numero_propiedad", `%${unidad}%`);
          if (signal) q = q.abortSignal(signal);
          const { data, error } = await q;
          if (error) throw error;
          meter((data ?? []).map((p: any) => aRef(p)));
        }
      }
    }
  }

  // 2) Solo unidad: número de unidad en cualquier proyecto.
  if (unidad && !palabras) {
    let q = sb
      .from("propiedades")
      .select(SELECT_PROPIEDAD)
      .ilike("numero_propiedad", `%${unidad}%`)
      .order("numero_propiedad")
      .limit(limite);
    if (signal) q = q.abortSignal(signal);
    const { data, error } = await q;
    if (error) throw error;
    meter((data ?? []).map((p: any) => aRef(p)));
  }

  // 3) Por cliente (comprador) cuando las letras no son un proyecto.
  if (palabras && !proyectoCoincide && out.size < limite) {
    let qp = sb
      .from("personas")
      .select("id")
      .or(`nombre_legal.ilike.%${palabras}%,nombre_comercial.ilike.%${palabras}%`)
      .limit(20);
    if (signal) qp = qp.abortSignal(signal);
    const { data: personas, error } = await qp;
    if (error) throw error;
    let refs = await propiedadesDePersonas((personas ?? []).map((p: any) => Number(p.id)), signal);
    if (unidad) refs = refs.filter((r) => r.etiqueta.toLowerCase().includes(unidad.toLowerCase()));
    meter(refs);
  }

  return Array.from(out.values());
}

// ─── Tabla de unión ───────────────────────────────────────────────────────────

/**
 * Propiedades activas de varios tickets. `null` = la tabla aún no existe en el ambiente
 * (PGRST205 / 42P01) o no se pudo leer: quien llama cae a `tickets.id_propiedad`.
 */
export async function fetchPropiedadesDeTickets(
  ticketIds: (string | number)[],
): Promise<Record<string, PropiedadRef[]> | null> {
  const ids = ticketIds.map(Number).filter(Number.isFinite);
  const out: Record<string, PropiedadRef[]> = {};
  if (!ids.length) return out;
  const filas: any[] = [];
  for (let i = 0; i < ids.length; i += 300) {
    const { data, error } = await sb
      .from("tickets_propiedades")
      .select("id_ticket, id_propiedad, id_cuenta_cobranza, fecha_creacion, id")
      .in("id_ticket", ids.slice(i, i + 300))
      .eq("activo", true)
      .order("fecha_creacion", { ascending: true })
      .order("id", { ascending: true });
    if (error) return null;
    filas.push(...(data ?? []));
  }
  if (!filas.length) return out;
  let desc: Map<string, PropiedadRef>;
  try {
    desc = await describirPropiedades(filas.map((f) => f.id_propiedad));
  } catch {
    desc = new Map();
  }
  for (const f of filas) {
    const d = desc.get(String(f.id_propiedad));
    (out[String(f.id_ticket)] ||= []).push({
      id: String(f.id_propiedad),
      etiqueta: d?.etiqueta ?? `Propiedad ${f.id_propiedad}`,
      idProyecto: d?.idProyecto ?? null,
      idCuentaCobranza: f.id_cuenta_cobranza != null ? String(f.id_cuenta_cobranza) : null,
    });
  }
  return out;
}

/** Error de PostgREST/Postgres por tabla inexistente (migración pendiente en el ambiente). */
export function esTablaInexistente(error: { code?: string | null } | null | undefined): boolean {
  return error?.code === "PGRST205" || error?.code === "42P01";
}

/**
 * Deja en `tickets_propiedades` exactamente `refs` (activas) para el ticket: las nuevas se
 * insertan o se reactivan (upsert por id_ticket+id_propiedad) y las que ya no están se apagan
 * (activo = false, sin DELETE). Devuelve "sin_tabla" si la migración aún no está.
 */
export async function sincronizarPropiedadesTicket(
  ticketId: string | number,
  refs: PropiedadRef[],
): Promise<"ok" | "sin_tabla" | { error: string }> {
  const idTicket = Number(ticketId);
  const { data: actuales, error: e1 } = await sb
    .from("tickets_propiedades")
    .select("id_propiedad, activo")
    .eq("id_ticket", idTicket);
  if (e1) return esTablaInexistente(e1) ? "sin_tabla" : { error: e1.message };

  const quedan = new Set(refs.map((r) => Number(r.id)));
  const apagar = (actuales ?? [])
    .filter((a: any) => a.activo && !quedan.has(Number(a.id_propiedad)))
    .map((a: any) => Number(a.id_propiedad));
  if (apagar.length) {
    const { error } = await sb
      .from("tickets_propiedades")
      .update({ activo: false })
      .eq("id_ticket", idTicket)
      .in("id_propiedad", apagar);
    if (error) return { error: error.message };
  }
  const activas = new Set(
    (actuales ?? []).filter((a: any) => a.activo).map((a: any) => Number(a.id_propiedad)),
  );
  const nuevas = refs.filter((r) => !activas.has(Number(r.id)));
  if (nuevas.length) {
    const { error } = await sb.from("tickets_propiedades").upsert(
      nuevas.map((r) => ({
        id_ticket: idTicket,
        id_propiedad: Number(r.id),
        id_cuenta_cobranza: r.idCuentaCobranza ? Number(r.idCuentaCobranza) : null,
        activo: true,
      })),
      { onConflict: "id_ticket,id_propiedad" },
    );
    if (error) return { error: error.message };
  }
  return "ok";
}
